import { useSearchParams } from "react-router-dom";

/**
 * React Router v6 replacement for the source app's TanStack Router
 * `useSearch()`/`navigate({ search })` pattern. Reads/writes the same
 * `range`/`office`/`compare`(/`segment`) URL search params every page shares,
 * via the standard `useSearchParams()` hook (string-based; booleans are
 * serialized as "true"/"false").
 */
export interface DashboardSearchValue {
  range: string;
  office: string;
  compare: boolean;
  segment: "all" | "PG" | "Corp";
}

export function useDashboardSearch(): DashboardSearchValue & {
  update: (patch: Partial<Record<"range" | "office" | "compare" | "segment", string | boolean>>) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const range = searchParams.get("range") ?? "90d";
  const office = searchParams.get("office") ?? "all";
  const compare = searchParams.get("compare") === "true";
  const segmentRaw = searchParams.get("segment");
  const segment = segmentRaw === "PG" || segmentRaw === "Corp" ? segmentRaw : "all";

  const update = (patch: Partial<Record<"range" | "office" | "compare" | "segment", string | boolean>>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        }
        return next;
      },
      { replace: true },
    );
  };

  return { range, office, compare, segment, update };
}
